import { formatDateToLocale } from "@/common/helpers/format-date";
import { SystemIdRow } from "@/components/detail-page/_components/system-id-row";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import {
  getFilledSystemIdRows,
  type FilledSystemIdRowEntry,
  type SystemIdRowEntry,
} from "@/components/detail-page/_helpers/system-id-rows";
import { FingerprintIcon, HashIcon, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export type SystemIdExternalIdentifier = Pick<SystemIdRowEntry, "id" | "label" | "value">;

interface SystemIdTabProps {
  uuid: string;
  technicalId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
  identifiers?: ReadonlyArray<SystemIdExternalIdentifier>;
}

export const SystemIdTab = ({
  uuid,
  technicalId,
  createdAt,
  updatedAt,
  deletedAt,
  identifiers = [],
}: SystemIdTabProps) => {
  const { t } = useTranslation("common");

  const externalRows = getFilledSystemIdRows(
    identifiers.map((identifier) => ({ ...identifier, mono: true, copyable: true })),
  );
  const internalRows = getFilledSystemIdRows([
    { id: "uuid", label: t("common:UUID"), value: uuid, mono: true, copyable: true },
    { id: "technical-id", label: t("common:TechnicalId"), value: technicalId, mono: true, copyable: true },
    { id: "created-at", label: t("common:CreatedAt"), value: createdAt && formatDateToLocale(createdAt) },
    { id: "updated-at", label: t("common:UpdatedAt"), value: updatedAt && formatDateToLocale(updatedAt) },
    { id: "deleted-at", label: t("common:Deleted"), value: deletedAt && formatDateToLocale(deletedAt) },
  ]);

  return (
    <DetailTabGrid>
      <SystemIdCard icon={FingerprintIcon} title={t("common:External")} rows={externalRows} />
      <SystemIdCard icon={HashIcon} title={t("common:Internal")} rows={internalRows} />
    </DetailTabGrid>
  );
};

function SystemIdCard({ icon, title, rows }: { icon: LucideIcon; title: string; rows: FilledSystemIdRowEntry[] }) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <DetailTabCard icon={icon} title={title}>
      <div className="flex flex-col gap-1">
        {rows.map(({ id, label, value, mono, copyable }) => (
          <SystemIdRow key={id} label={label} value={value} mono={mono} copyable={copyable} />
        ))}
      </div>
    </DetailTabCard>
  );
}
